import React, { useState, useEffect, useRef } from "react";
import { X, Cookie, ChevronDown, ChevronUp, Shield, BarChart2, Info } from "lucide-react";

const STORAGE_KEY = "komorotel_cookie_consent";
const GA_ID = "G-SWR37BWG2P"; // Remplacer par ton ID Google Analytics

// ─── Traductions ───────────────────────────────────────────────────────────
const TRANSLATIONS = {
  fr: {
    title: "Préférences cookies",
    description:
      "Nous utilisons des cookies pour améliorer votre expérience sur Komorotel. Certains sont indispensables au fonctionnement du site, d'autres nous aident à comprendre comment il est utilisé.",
    learnMore: "En savoir plus",
    customize: "Personnaliser",
    hideDetails: "Masquer les détails",
    savePreferences: "Enregistrer mes préférences",
    rejectAll: "Tout refuser",
    acceptAll: "Tout accepter",
    alwaysActive: "Toujours actif",
    essential: "Cookies essentiels",
    essentialDesc: "Authentification, sécurité, préférences. Obligatoires au bon fonctionnement du site.",
    essentialTooltip:
      "Ces cookies sont indispensables au fonctionnement du site et ne peuvent pas être désactivés.",
    analytics: "Cookies analytiques",
    analyticsDesc: "Google Analytics — nous aide à améliorer la plateforme (données anonymisées).",
  },
  en: {
    title: "Cookie preferences",
    description:
      "We use cookies to improve your experience on Komorotel. Some are essential for the site to work; others help us understand how visitors use it.",
    learnMore: "Learn more",
    customize: "Customize",
    hideDetails: "Hide details",
    savePreferences: "Save my preferences",
    rejectAll: "Reject all",
    acceptAll: "Accept all",
    alwaysActive: "Always active",
    essential: "Essential cookies",
    essentialDesc: "Authentication, security, preferences. Required for the site to work.",
    essentialTooltip:
      "These cookies are required for the site to work and cannot be disabled.",
    analytics: "Analytics cookies",
    analyticsDesc: "Google Analytics — helps us improve the platform (anonymised data).",
  },
};

// ─── Google Analytics ──────────────────────────────────────────────────────
function loadGoogleAnalytics() {
  if (window._gaLoaded) return;
  window._gaLoaded = true;
  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  document.head.appendChild(script);
  window.dataLayer = window.dataLayer || [];
  function gtag() { window.dataLayer.push(arguments); }
  window.gtag = gtag;
  gtag("js", new Date());
  gtag("config", GA_ID, { anonymize_ip: true });
}

// ─── Helpers ───────────────────────────────────────────────────────────────
function getSavedConsent() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved) : null;
  } catch {
    return null;
  }
}

function saveConsent(preferences) {
  const consent = {
    necessary: true,
    analytics: preferences.analytics,
    savedAt: new Date().toISOString(),
  };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(consent));
  if (consent.analytics) loadGoogleAnalytics();
  return consent;
}

// ─── Tooltip ───────────────────────────────────────────────────────────────
function Tooltip({ text, children }) {
  const [show, setShow] = useState(false);
  const ref = useRef(null);

  return (
    <div className="relative inline-flex" ref={ref}>
      <div
        onMouseEnter={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
        onFocus={() => setShow(true)}
        onBlur={() => setShow(false)}
      >
        {children}
      </div>
      {show && (
        <div
          className="absolute bottom-full right-0 mb-2 w-56 rounded-xl px-3 py-2 text-xs text-white shadow-lg z-50"
          style={{ backgroundColor: "#1a1a1a" }}
        >
          {text}
          <div
            className="absolute top-full right-3 border-4 border-transparent"
            style={{ borderTopColor: "#1a1a1a" }}
          />
        </div>
      )}
    </div>
  );
}

// ─── Toggle ────────────────────────────────────────────────────────────────
function Toggle({ enabled, onChange, locked = false }) {
  return (
    <button
      onClick={() => !locked && onChange(!enabled)}
      className="shrink-0 w-10 h-5 rounded-full flex items-center px-0.5 transition-colors duration-200"
      style={{
        backgroundColor: enabled ? "#3A9D23" : "#D1D5DB",
        cursor: locked ? "not-allowed" : "pointer",
        opacity: locked ? 0.7 : 1,
      }}
      aria-checked={enabled}
      role="switch"
      disabled={locked}
    >
      <div
        className="w-4 h-4 bg-white rounded-full transition-transform duration-200"
        style={{ transform: enabled ? "translateX(20px)" : "translateX(0)" }}
      />
    </button>
  );
}

// ─── Main Component ────────────────────────────────────────────────────────
export default function CookieBanner({ lang = "fr" }) {
  const t = TRANSLATIONS[lang] || TRANSLATIONS.fr;

  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [analyticsEnabled, setAnalyticsEnabled] = useState(true);

  useEffect(() => {
    const saved = getSavedConsent();
    if (!saved) {
      setTimeout(() => setVisible(true), 800);
    } else if (saved.analytics) {
      loadGoogleAnalytics();
    }
  }, []);

  const handleAcceptAll = () => {
    saveConsent({ analytics: true });
    setVisible(false);
  };

  const handleRejectAll = () => {
    saveConsent({ analytics: false });
    setVisible(false);
  };

  const handleSavePreferences = () => {
    saveConsent({ analytics: analyticsEnabled });
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/20 z-40 backdrop-blur-[1px]" />

      {/* Banner */}
      <div
        className="fixed bottom-4 left-4 right-4 md:left-auto md:right-6 md:bottom-6 md:w-[420px] z-50
                   bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden"
        style={{ fontFamily: "'DM Sans', sans-serif" }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-5 pb-3">
          <div className="flex items-center gap-2">
            <Cookie size={20} style={{ color: "#3A9D23" }} />
            <span className="font-semibold text-gray-800 text-[15px]">{t.title}</span>
          </div>
          <button
            onClick={handleRejectAll}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Fermer"
          >
            <X size={18} />
          </button>
        </div>

        {/* Description */}
        <div className="px-5 pb-2">
          <p className="text-gray-500 text-sm leading-relaxed">
            {t.description}{" "}
            <a
              href="/politique-de-confidentialite"
              className="underline hover:text-gray-700 transition-colors"
              style={{ color: "#3A9D23" }}
            >
              {t.learnMore}
            </a>
          </p>
        </div>

        {/* Customize toggle */}
        <div className="px-5 pb-3">
          <button
            onClick={() => setShowDetails(!showDetails)}
            className="flex items-center gap-1 text-sm font-medium mt-1 transition-colors"
            style={{ color: "#3A9D23" }}
          >
            {showDetails ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
            {showDetails ? t.hideDetails : t.customize}
          </button>

          {showDetails && (
            <div className="mt-3 space-y-4 border-t border-gray-100 pt-3">

              {/* Essential — verrouillé */}
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-2">
                  <Shield size={16} className="mt-0.5 shrink-0 text-gray-400" />
                  <div>
                    <p className="text-sm font-medium text-gray-700">{t.essential}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{t.essentialDesc}</p>
                  </div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <span className="text-xs text-gray-400 whitespace-nowrap">{t.alwaysActive}</span>
                  <Tooltip text={t.essentialTooltip}>
                    <Info size={13} className="text-gray-300 cursor-help" />
                  </Tooltip>
                  <Toggle enabled={true} locked={true} onChange={() => {}} />
                </div>
              </div>

              {/* Analytics */}
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-2">
                  <BarChart2 size={16} className="mt-0.5 shrink-0 text-gray-400" />
                  <div>
                    <p className="text-sm font-medium text-gray-700">{t.analytics}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{t.analyticsDesc}</p>
                  </div>
                </div>
                <Toggle
                  enabled={analyticsEnabled}
                  onChange={setAnalyticsEnabled}
                />
              </div>

              {/* Save preferences */}
              <button
                onClick={handleSavePreferences}
                className="w-full py-2 rounded-xl text-sm font-medium border transition-colors"
                style={{ borderColor: "#3A9D23", color: "#3A9D23" }}
                onMouseEnter={e => { e.currentTarget.style.backgroundColor = "#f0faf0"; }}
                onMouseLeave={e => { e.currentTarget.style.backgroundColor = "transparent"; }}
              >
                {t.savePreferences}
              </button>
            </div>
          )}
        </div>

        {/* CTA buttons */}
        <div className="px-5 pb-5 flex gap-2 mt-1">
          <button
            onClick={handleRejectAll}
            className="flex-1 py-2.5 rounded-xl text-sm font-medium border border-gray-200 text-gray-500
                       hover:border-gray-300 hover:text-gray-700 transition-colors"
          >
            {t.rejectAll}
          </button>
          <button
            onClick={handleAcceptAll}
            className="flex-1 py-2.5 rounded-xl text-sm font-medium text-white transition-opacity hover:opacity-90"
            style={{ backgroundColor: "#3A9D23" }}
          >
            {t.acceptAll}
          </button>
        </div>
      </div>
    </>
  );
}

// ─── Hook utilitaire ───────────────────────────────────────────────────────
export function useCookieConsent() {
  const consent = getSavedConsent();
  return {
    hasConsented: !!consent,
    analyticsEnabled: consent?.analytics ?? false,
    resetConsent: () => {
      localStorage.removeItem(STORAGE_KEY);
      window.location.reload();
    },
  };
}